import type { IntakeData } from "../types";
import type { FormCatalogKind, FormConfigDiagnostic, FormConfigVersion, FormRule, FormRuleCondition, FormRuleContext, FormRuleEffect, FormRuleFieldCondition, OneOrMany } from "./types";

export function resolveRuleEffect(targetId: string, context: FormRuleContext, config: FormConfigVersion, data?: IntakeData, catalog = config.catalog): { effect: FormRuleEffect; appliedRules: FormRule[]; diagnostics: FormConfigDiagnostic[] } {
  const diagnostics: FormConfigDiagnostic[] = [];
  const appliedRules = config.rules
    .filter((rule) => rule.targetId === targetId && matchesCondition(rule.condition, context, data))
    .sort((left, right) => ruleRank(left) - ruleRank(right));
  const effect = appliedRules.reduce<FormRuleEffect>((merged, rule) => mergeRuleEffect(merged, rule.effect), {});
  const catalogIds = new Set(catalog.map((item) => item.id));
  if (appliedRules.length > 0 && !catalogIds.has(targetId)) {
    diagnostics.push({ level: "error", code: "missing_target", targetId, message: `Rule target is not registered in the form catalog: ${targetId}` });
  }
  return { effect, appliedRules, diagnostics };
}

function mergeRuleEffect(base: FormRuleEffect, next: FormRuleEffect): FormRuleEffect {
  return {
    ...base,
    ...next,
    allowedOptionIds: next.allowedOptionIds ? [...next.allowedOptionIds] : base.allowedOptionIds,
  };
}

function matchesCondition(condition: FormRuleCondition, context: FormRuleContext, data?: IntakeData): boolean {
  if (condition.domain && !matchesOneOrMany(condition.domain, context.domain)) return false;
  if (condition.categoryId && !matchesOneOrMany(condition.categoryId, context.categoryId)) return false;
  if (condition.contractType && !matchesOneOrMany(condition.contractType, context.contractType ?? "")) return false;
  if (condition.field && !matchesFieldCondition(condition.field, data)) return false;
  return true;
}

function matchesOneOrMany<T extends string>(expected: OneOrMany<T>, actual: string): boolean {
  return Array.isArray(expected) ? expected.includes(actual as T) : expected === actual;
}

function matchesFieldCondition(condition: FormRuleFieldCondition, data?: IntakeData): boolean {
  const value = data ? getPathValue(data, condition.path) : undefined;
  const text = String(value ?? "");
  const expected = condition.value;
  switch (condition.operator) {
    case "equals": return String(value ?? "") === String(expected ?? "");
    case "notEquals": return String(value ?? "") !== String(expected ?? "");
    case "includes": return splitMultiValue(value).includes(String(expected ?? ""));
    case "notIncludes": return !splitMultiValue(value).includes(String(expected ?? ""));
    case "isTrue": return value === true;
    case "isFalse": return value !== true;
    case "isBlank": return !text.trim();
    case "isNotBlank": return Boolean(text.trim());
    default: return true;
  }
}

function splitMultiValue(value: unknown): string[] {
  return String(value ?? "")
    .split(/[\n,]/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function getPathValue(source: unknown, path: string): unknown {
  return path.split(".").reduce<unknown>((current, key) => {
    if (!current || typeof current !== "object") return undefined;
    return (current as Record<string, unknown>)[key];
  }, source);
}

function ruleRank(rule: FormRule): number {
  const conditionWeight = (rule.condition.domain ? 10 : 0) + (rule.condition.categoryId ? 20 : 0) + (rule.condition.contractType ? 30 : 0) + (rule.condition.field ? 40 : 0);
  return conditionWeight + (rule.priority ?? 0);
}

export function validateFormConfig(config: FormConfigVersion): FormConfigDiagnostic[] {
  const diagnostics: FormConfigDiagnostic[] = [];
  const catalogById = new Map(config.catalog.map((item) => [item.id, item]));
  for (const rule of config.rules) {
    const catalogItem = catalogById.get(rule.targetId);
    if (!catalogItem) {
      diagnostics.push({ level: "error", code: "missing_target", ruleId: rule.id, targetId: rule.targetId, message: `Rule '${rule.id}' references unknown target '${rule.targetId}'.` });
      continue;
    }
    if (catalogItem.kind !== rule.targetKind) {
      diagnostics.push({ level: "error", code: "target_kind_mismatch", ruleId: rule.id, targetId: rule.targetId, message: `Rule '${rule.id}' declares ${rule.targetKind} but catalog target is ${catalogItem.kind}.` });
    }
    for (const effectKey of Object.keys(rule.effect) as Array<keyof FormRuleEffect>) {
      if (!isSupportedEffect(catalogItem.kind, effectKey)) {
        diagnostics.push({ level: "error", code: "unsupported_effect", ruleId: rule.id, targetId: rule.targetId, message: `Rule '${rule.id}' uses unsupported effect '${effectKey}' on '${rule.targetId}'.` });
      }
    }
    if (rule.effect.visible === false && rule.effect.required === true) {
      diagnostics.push({ level: "error", code: "hidden_required", ruleId: rule.id, targetId: rule.targetId, message: `Rule '${rule.id}' makes a hidden target required.` });
    }
    for (const optionId of rule.effect.allowedOptionIds ?? []) {
      const optionItem = catalogById.get(optionId);
      if (!optionItem) {
        diagnostics.push({ level: "error", code: "unknown_option", ruleId: rule.id, targetId: optionId, message: `Rule '${rule.id}' references unknown option '${optionId}'.` });
      } else if (optionItem.kind !== "option") {
        diagnostics.push({ level: "error", code: "invalid_option_target", ruleId: rule.id, targetId: optionId, message: `Rule '${rule.id}' references non-option target '${optionId}' in allowedOptionIds.` });
      }
    }
  }
  return dedupeDiagnostics(diagnostics);
}

function isSupportedEffect(kind: FormCatalogKind, effectKey: keyof FormRuleEffect): boolean {
  if (effectKey === "allowedOptionIds") return ["field", "repeaterField"].includes(kind);
  if (effectKey === "required") return ["field", "repeaterField", "upload"].includes(kind);
  if (effectKey === "visible") return ["step", "field", "guard", "option", "repeater", "repeaterField", "upload"].includes(kind);
  if (effectKey === "label") return ["step", "field", "guard", "option", "repeater", "repeaterField", "upload"].includes(kind);
  if (effectKey === "title") return ["step", "field", "option", "repeater", "repeaterField", "upload"].includes(kind);
  if (effectKey === "description") return ["step", "guard", "option", "repeater", "upload"].includes(kind);
  if (["placeholder", "helperText", "checkboxText", "defaultValue"].includes(effectKey)) return ["field", "repeaterField"].includes(kind);
  if (["addLabel", "minItems"].includes(effectKey)) return kind === "repeater";
  return false;
}

export function sanitizeRuleOptionIds(rules: FormRule[], catalog: Array<{ id: string; kind: FormCatalogKind }>): FormRule[] {
  const knownOptionIds = new Set(catalog.filter((item) => item.kind === "option").map((item) => item.id));
  return rules.map((rule) => {
    const allowed = rule.effect.allowedOptionIds;
    if (!allowed?.length) return rule;
    const kept = allowed.filter((optionId) => knownOptionIds.has(optionId));
    if (kept.length === allowed.length) return rule;
    const effect = { ...rule.effect };
    if (kept.length) effect.allowedOptionIds = kept;
    else delete effect.allowedOptionIds;
    return { ...rule, effect };
  });
}

export function uniqueRules(rules: FormRule[]): FormRule[] {
  const seen = new Set<string>();
  return rules.filter((rule) => {
    if (seen.has(rule.id)) return false;
    seen.add(rule.id);
    return true;
  });
}

export function dedupeDiagnostics(diagnostics: FormConfigDiagnostic[]): FormConfigDiagnostic[] {
  const seen = new Set<string>();
  return diagnostics.filter((diagnostic) => {
    const key = `${diagnostic.level}:${diagnostic.code}:${diagnostic.ruleId ?? ""}:${diagnostic.targetId ?? ""}:${diagnostic.message}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}
